import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dropdown, Icon, Loader, Message } from 'semantic-ui-react';
import { Link, useNavigate } from 'react-router-dom';
import {
  API,
  readAccessTokenPreference,
  showError,
  showSuccess,
  showWarning,
} from '../../helpers';
import './Playground.css';

const VIDEO_HISTORY_KEY = 'sinoaw_playground_video_history';
const MAX_PROMPT_LENGTH = 3000;
const MAX_MATERIAL_COUNT = 15;

const videoPromptTemplates = [
  {
    key: 'ad',
    title: '产品广告',
    prompt:
      '写实产品广告，第一人称视角展示一杯夏日果茶，从原料、制作到成品递到镜头前，节奏轻快，画面明亮，品牌标签清晰。',
  },
  {
    key: 'dance',
    title: '舞蹈短片',
    prompt:
      '一位年轻女生在城市夜景天台跳舞，镜头跟随身体动作缓慢推进，霓虹灯反射在地面，动作自然流畅，电影感运镜。',
  },
  {
    key: 'talking',
    title: '口播讲解',
    prompt:
      '半身口播视频，人物面对镜头介绍产品亮点，背景简洁高级，表情自然，镜头稳定，适合社媒宣传。',
  },
  {
    key: 'display',
    title: '产品展示',
    prompt:
      '高级产品展示视频，产品在柔和灯光下旋转，镜头切换细节特写，背景干净，质感真实，适合电商详情页。',
  },
];

const videoKeywords = [
  'video',
  'wan',
  'kling',
  'hailuo',
  'jimeng',
  'sora',
  'vidu',
  'veo',
  'runway',
  'seedance',
  '视频',
];

const nonChatKeywords = [
  'embedding',
  'rerank',
  'whisper',
  'tts',
  'audio',
  'realtime',
  'image',
  'dall-e',
  'midjourney',
  'mj-',
  'stable-diffusion',
  'video',
  'kling',
  'sora',
  'wan',
  'veo',
  'vidu',
];

const languageModelOverrides = ['doubao-realtime'];

const ratioOptions = [
  { key: '16:9', text: '16:9', value: '16:9' },
  { key: '9:16', text: '9:16', value: '9:16' },
  { key: '1:1', text: '1:1', value: '1:1' },
  { key: '4:3', text: '4:3', value: '4:3' },
  { key: '3:4', text: '3:4', value: '3:4' },
];

const resolutionOptions = [
  { key: '480P', text: '480P', value: '480P' },
  { key: '720P', text: '720P', value: '720P' },
  { key: '1080P', text: '1080P', value: '1080P' },
];

const styleOptions = [
  { key: '不限', text: '不限', value: '不限' },
  { key: '写实', text: '写实', value: '写实' },
  { key: '电影感', text: '电影感', value: '电影感' },
  { key: '动漫', text: '动漫', value: '动漫' },
  { key: '水彩', text: '水彩', value: '水彩' },
  { key: '赛博朋克', text: '赛博朋克', value: '赛博朋克' },
];

const materialTypeOptions = [
  { key: 'image_url', text: '图片 URL', value: 'image_url' },
  { key: 'video_url', text: '视频 URL', value: 'video_url' },
  { key: 'audio_url', text: '音频 URL', value: 'audio_url' },
];

function readLocalHistory() {
  try {
    return JSON.parse(localStorage.getItem(VIDEO_HISTORY_KEY) || '[]');
  } catch (error) {
    return [];
  }
}

function saveLocalHistory(items) {
  localStorage.setItem(VIDEO_HISTORY_KEY, JSON.stringify(items.slice(0, 20)));
}

function tokenAuthorization(token) {
  if (!token?.key) {
    return '';
  }
  return token.key.startsWith('sk-') ? token.key : `sk-${token.key}`;
}

function selectPreferredToken(tokens, preference) {
  const preferredId = Number(preference?.id || 0);
  if (preferredId > 0) {
    const preferredToken = tokens.find((token) => token.id === preferredId);
    if (preferredToken) {
      return preferredToken;
    }
  }
  return tokens.find((token) => token.status === 1) || tokens[0] || null;
}

function modelNameOf(item) {
  if (typeof item === 'string') {
    return item;
  }
  return item?.id || item?.model || item?.name || '';
}

function parseModelPayload(payload) {
  const list = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload)
      ? payload
      : [];

  return [...new Set(list.map(modelNameOf).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b)
  );
}

function parseModelCatalogPayload(payload) {
  const list = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload)
      ? payload
      : [];
  const seen = new Set();
  return list
    .map((item) => {
      const name = modelNameOf(item);
      if (!name || seen.has(name)) {
        return null;
      }
      seen.add(name);
      return {
        name,
        displayName: item?.display_name || item?.displayName || name,
        provider: item?.provider || '',
        modality: item?.modality || 'chat',
        apiFormat: item?.api_format || item?.apiFormat || '',
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name));
}

function tokenAllowedModels(token) {
  const raw = token?.models || '';
  if (!raw.trim()) {
    return [];
  }
  return raw
    .split(',')
    .map((model) => model.trim())
    .filter(Boolean);
}

function applyTokenModelScope(models, token) {
  const allowedModels = tokenAllowedModels(token);
  if (!allowedModels.length) {
    return models;
  }
  const allowedSet = new Set(allowedModels);
  const scopedModels = models.filter((model) => allowedSet.has(model));
  return scopedModels.length ? scopedModels : allowedModels;
}

function applyTokenCatalogScope(catalog, token) {
  const allowedModels = tokenAllowedModels(token);
  if (!allowedModels.length) {
    return catalog;
  }
  const allowedSet = new Set(allowedModels);
  const scopedCatalog = catalog.filter((model) => allowedSet.has(model.name));
  if (scopedCatalog.length) {
    return scopedCatalog;
  }
  return allowedModels.map((name) => ({
    name,
    displayName: name,
    provider: '',
    modality: modelIncludes(name, videoKeywords) ? 'video' : 'chat',
    apiFormat: '',
  }));
}

function modelIncludes(model, keywords) {
  const lower = model.toLowerCase();
  return keywords.some((keyword) => lower.includes(keyword));
}

function isVideoModelMeta(model) {
  return (
    model?.modality === 'video' ||
    model?.apiFormat === 'maas_seedance' ||
    model?.apiFormat === 'seedance_rest' ||
    modelIncludes(model?.name || '', videoKeywords)
  );
}

function isLanguageModel(model) {
  return (
    modelIncludes(model, languageModelOverrides) ||
    !modelIncludes(model, nonChatKeywords)
  );
}

function isRealtimeLanguageModel(model) {
  return modelIncludes(model || '', languageModelOverrides);
}

function realtimeURL(model) {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const url = new URL(`${protocol}//${window.location.host}/v1/realtime/dialogue`);
  url.searchParams.set('model', model);
  return url.toString();
}

function realtimeSessionConfig() {
  return JSON.stringify({
    dialog: {
      dialog_id: '',
      bot_name: 'sinoaw',
    },
    tts: {
      audio_config: {
        channel: 1,
        format: 'pcm_s16le',
        sample_rate: 24000,
      },
    },
    asr: {
      audio_info: {
        format: 'pcm',
        sample_rate: 16000,
        channel: 1,
      },
    },
  });
}

function extractErrorMessage(error) {
  const message =
    (typeof error === 'string' ? error : '') ||
    error?.response?.data?.error?.message ||
    error?.response?.data?.message ||
    error?.message ||
    '调用失败';
  const lowerMessage = message.toLowerCase();
  if (
    message.includes('额度') ||
    message.includes('余额') ||
    lowerMessage.includes('quota') ||
    lowerMessage.includes('insufficient') ||
    lowerMessage.includes('request failed with status code 403') ||
    (error?.response?.status === 403 && !message)
  ) {
    return '用户余额不足，请充值后再试';
  }
  return (
    message
  );
}

function testRealtimeConnection(model, token) {
  return new Promise((resolve, reject) => {
    const sessionId = `playground-${Date.now()}`;
    let settled = false;
    let readyTimer = null;
    const socket = new WebSocket(realtimeURL(model), ['sinoaw-realtime', token]);

    const finish = (error) => {
      if (settled) return;
      settled = true;
      if (readyTimer) {
        clearTimeout(readyTimer);
      }
      if (socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ session_id: sessionId, event_id: 102 }));
        socket.send(JSON.stringify({ session_id: sessionId, event_id: 2 }));
      }
      socket.close();
      if (error) {
        reject(error);
      } else {
        resolve();
      }
    };

    socket.onopen = () => {
      socket.send(JSON.stringify({ session_id: sessionId, event_id: 1 }));
      socket.send(
        JSON.stringify({
          session_id: sessionId,
          event_id: 100,
          content: realtimeSessionConfig(),
        })
      );
      readyTimer = setTimeout(() => finish(), 1800);
    };

    socket.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        const message = payload?.error?.message || payload?.message;
        if (payload?.event_id === -1 || message) {
          finish(new Error(message || 'Realtime 上游连接失败'));
        }
      } catch (error) {
        finish(new Error(event.data || 'Realtime 返回格式异常'));
      }
    };

    socket.onerror = () => {
      finish(new Error('Realtime WebSocket 连接失败'));
    };

    socket.onclose = () => {
      if (!settled) {
        finish(new Error('Realtime WebSocket 已关闭'));
      }
    };

    setTimeout(() => finish(new Error('Realtime WebSocket 连接超时')), 10000);
  });
}

function makeOptions(models, catalogByName = new Map()) {
  return models.map((model) => ({
    key: model,
    text: (() => {
      const meta = catalogByName.get(model);
      if (!meta?.displayName || meta.displayName === model) {
        return model;
      }
      return `${meta.displayName} · ${model}`;
    })(),
    value: model,
    description: catalogByName.get(model)?.provider || undefined,
  }));
}

function formatTime(timestamp) {
  return new Date(timestamp).toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatNumber(value) {
  return new Intl.NumberFormat('zh-CN').format(Number(value || 0));
}

function formatMaterialSize(value) {
  const size = Number(value || 0);
  if (size >= 1024 * 1024) {
    return `${(size / 1024 / 1024).toFixed(1)} MB`;
  }
  if (size >= 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }
  return `${size} B`;
}

function formatCurrency(value) {
  return Number(value || 0).toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
}

function formatRatio(value) {
  return Number(value || 0).toFixed(5).replace(/0+$/, '').replace(/\.$/, '');
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function taskStatusText(status) {
  const map = {
    queued: '排队中',
    processing: '生成中',
    succeeded: '成功',
    failed: '失败',
    refunded: '已退款',
    timeout: '已超时',
  };
  return map[status] || status || '已提交';
}

function parseTaskJSON(task) {
  if (!task?.request_json) return {};
  try {
    return JSON.parse(task.request_json);
  } catch (error) {
    return {};
  }
}

function taskToHistoryRecord(task) {
  const request = parseTaskJSON(task);
  return {
    id: task.task_id,
    taskId: task.task_id,
    prompt: task.prompt || request.prompt || '',
    model: task.model_name,
    status: taskStatusText(task.status),
    statusRaw: task.status,
    createdAt: Number(task.submitted_time || 0) * 1000 || Date.now(),
    params: {
      ratio: task.ratio || request.ratio || '16:9',
      resolution: task.resolution || request.resolution || '720p',
      duration: task.duration || request.duration || 11,
      style: '不限',
      audio: task.generate_audio,
    },
    materials: request.materials?.map((item) => item.url) || [],
    videoUrl: task.local_video_url || task.upstream_video_url || '',
    upstreamVideoUrl: task.upstream_video_url || '',
    billing: {
      input_scene: task.input_scene,
      resolution: task.resolution,
      model_tokens: task.model_tokens,
      token_unit_price: task.token_unit_price,
      resource_tokens: task.resource_tokens,
      amount_rmb: task.amount_rmb,
      offset_ratio: task.offset_ratio,
      estimated: task.estimated,
    },
    warning: task.status === 'succeeded' ? task.error_message || '' : '',
    error: task.status === 'succeeded' ? '' : task.error_message || '',
  };
}

function defaultRoleForMaterialType(type) {
  if (type === 'audio_url') return 'reference_audio';
  if (type === 'video_url') return 'reference_video';
  return 'reference_image';
}

function iconForMaterialType(type) {
  if (type === 'audio_url') return 'music';
  if (type === 'video_url') return 'film';
  return 'image outline';
}

function materialAssetName(asset) {
  return asset?.name || asset?.original_name || `素材 #${asset?.id || '-'}`;
}

function lastMaterialMentionIndex(value) {
  return Math.max(value.lastIndexOf('@'), value.lastIndexOf('＠'));
}

function hasMaterialMention(value) {
  return lastMaterialMentionIndex(value) >= 0;
}

function isHTTPURL(value) {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch (error) {
    return false;
  }
}

const Playground = () => {
  const navigate = useNavigate();
  const mountedRef = useRef(true);
  const materialUploadInputRef = useRef(null);
  const materialUploadTargetRef = useRef('material');
  const [mode, setMode] = useState('video');
  const [loading, setLoading] = useState(true);
  const [tokens, setTokens] = useState([]);
  const [models, setModels] = useState([]);
  const [modelCatalog, setModelCatalog] = useState([]);
  const [selectedChatModel, setSelectedChatModel] = useState('');
  const [selectedVideoModel, setSelectedVideoModel] = useState('');
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [videoPrompt, setVideoPrompt] = useState('');
  const [materials, setMaterials] = useState([]);
  const [backgroundPollingTaskId, setBackgroundPollingTaskId] = useState('');
  const [materialDraft, setMaterialDraft] = useState({
    type: 'image_url',
    url: '',
  });
  const [firstFrameUrl, setFirstFrameUrl] = useState('');
  const [lastFrameUrl, setLastFrameUrl] = useState('');
  const [videoGenerating, setVideoGenerating] = useState(false);
  const [videoEstimate, setVideoEstimate] = useState(null);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [videoParams, setVideoParams] = useState({
    ratio: '16:9',
    resolution: '720P',
    duration: 11,
    style: '不限',
    audio: true,
  });
  const [videoHistory, setVideoHistory] = useState(readLocalHistory);
  const [latestVideoResult, setLatestVideoResult] = useState(null);
  const [materialLibrary, setMaterialLibrary] = useState([]);
  const [materialLibraryLoading, setMaterialLibraryLoading] = useState(false);
  const [uploadingMaterial, setUploadingMaterial] = useState(false);
  const [materialReferenceOpen, setMaterialReferenceOpen] = useState(false);
  const [savedTokenPreference, setSavedTokenPreference] = useState(readAccessTokenPreference);

  const activeToken = useMemo(
    () => selectPreferredToken(tokens, savedTokenPreference),
    [tokens, savedTokenPreference]
  );
  const savedTokenSelected =
    Boolean(savedTokenPreference?.id) && activeToken?.id === savedTokenPreference.id;
  const bearerToken = tokenAuthorization(activeToken);
  const catalogByName = useMemo(
    () => new Map(modelCatalog.map((model) => [model.name, model])),
    [modelCatalog]
  );

  const videoModels = useMemo(() => {
    const catalogVideoModels = modelCatalog.filter(isVideoModelMeta).map((model) => model.name);
    return catalogVideoModels.length
      ? catalogVideoModels
      : models.filter((model) => modelIncludes(model, videoKeywords));
  }, [modelCatalog, models]);
  const chatModels = useMemo(() => {
    const catalogChatModels = modelCatalog
      .filter((model) => !isVideoModelMeta(model) && (model.modality || 'chat') === 'chat')
      .map((model) => model.name);
    if (catalogChatModels.length) {
      return catalogChatModels;
    }
    const filtered = models.filter(isLanguageModel);
    return filtered.length ? filtered : models.filter((model) => !modelIncludes(model, ['realtime']));
  }, [modelCatalog, models]);

  const modelOptions = useMemo(() => makeOptions(chatModels, catalogByName), [catalogByName, chatModels]);
  const videoModelOptions = useMemo(() => makeOptions(videoModels, catalogByName), [catalogByName, videoModels]);
  const materialReferenceActive = materialReferenceOpen || hasMaterialMention(videoPrompt);
  const materialReferenceQuery = useMemo(() => {
    const atIndex = lastMaterialMentionIndex(videoPrompt);
    if (atIndex < 0) {
      return '';
    }
    const tail = videoPrompt.slice(atIndex + 1);
    if (/[\s\n，。；、,.!?！？]/.test(tail)) {
      return '';
    }
    return tail.trim().toLowerCase();
  }, [videoPrompt]);
  const materialReferenceOptions = useMemo(() => {
    if (!materialReferenceActive) {
      return [];
    }
    const query = materialReferenceQuery;
    return materialLibrary
      .filter((asset) => {
        if (!query) {
          return true;
        }
        const searchable = [
          asset.name,
          asset.original_name,
          asset.mime_type,
          asset.type,
        ].join(' ').toLowerCase();
        return searchable.includes(query);
      })
      .slice(0, 8);
  }, [materialLibrary, materialReferenceActive, materialReferenceQuery]);

  const loadMaterialLibrary = useCallback(async () => {
    setMaterialLibraryLoading(true);
    try {
      const res = await API.get('/api/materials?p=0&type=all');
      if (res.data?.success) {
        setMaterialLibrary(res.data.data || []);
      } else {
        showError(res.data?.message || '素材库加载失败');
      }
    } catch (error) {
      showError(error?.response?.data?.message || error?.message || '素材库加载失败');
    } finally {
      setMaterialLibraryLoading(false);
    }
  }, []);

  const loadConsoleData = useCallback(async () => {
    setLoading(true);
    try {
      const tokenRes = await API.get('/api/token/?p=0&order=');
      const tokenList = tokenRes?.data?.success ? tokenRes.data.data || [] : [];
      setTokens(tokenList);
      const tokenPreference = readAccessTokenPreference();
      setSavedTokenPreference(tokenPreference);

      const token = selectPreferredToken(tokenList, tokenPreference);
      let nextModels = [];
      let nextCatalog = [];
      const catalogRes = await API.get('/api/user/model_catalog');
      if (catalogRes?.data?.success) {
        nextCatalog = parseModelCatalogPayload(catalogRes.data.data || []);
      }
      if (!nextCatalog.length) {
        const availableRes = await API.get('/api/user/available_models');
        if (availableRes?.data?.success) {
          nextModels = parseModelPayload(availableRes.data.data || []);
          nextCatalog = nextModels.map((name) => ({
            name,
            displayName: name,
            provider: '',
            modality: modelIncludes(name, videoKeywords) ? 'video' : 'chat',
            apiFormat: '',
          }));
        }
      }
      if (token) {
        nextCatalog = applyTokenCatalogScope(nextCatalog, token);
      }
      nextModels = applyTokenModelScope(nextCatalog.map((model) => model.name), token);

      setModelCatalog(nextCatalog);
      setModels(nextModels);
      setSelectedChatModel((current) => current || nextModels.find(isLanguageModel) || '');
      setSelectedVideoModel((current) => current || nextCatalog.find(isVideoModelMeta)?.name || nextModels.find((model) => modelIncludes(model, videoKeywords)) || '');
      const tasksRes = await API.get('/api/video-generation/tasks?p=0');
      if (tasksRes?.data?.success) {
        const serverHistory = (tasksRes.data.data || []).map(taskToHistoryRecord);
        const localHistory = readLocalHistory().filter(
          (item) => !serverHistory.some((serverItem) => serverItem.id === item.id)
        );
        const mergedHistory = [...serverHistory, ...localHistory].slice(0, 20);
        setVideoHistory(mergedHistory);
        saveLocalHistory(mergedHistory);
      }
      const materialsRes = await API.get('/api/materials?p=0&type=all');
      if (materialsRes?.data?.success) {
        setMaterialLibrary(materialsRes.data.data || []);
      }
    } catch (error) {
      showError(error?.response?.data?.message || error?.message || '加载体验中心失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConsoleData();
  }, [loadConsoleData]);

  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  useEffect(() => {
    if (chatModels.length && (!selectedChatModel || !chatModels.includes(selectedChatModel))) {
      setSelectedChatModel(chatModels[0]);
    }
  }, [chatModels, selectedChatModel]);

  useEffect(() => {
    if (videoModels.length && (!selectedVideoModel || !videoModels.includes(selectedVideoModel))) {
      setSelectedVideoModel(videoModels[0]);
    }
  }, [selectedVideoModel, videoModels]);

  const updateVideoParam = (name, value) => {
    setVideoParams((current) => ({ ...current, [name]: value }));
  };

  const updateMaterialDraft = (name, value) => {
    setMaterialDraft((current) => ({ ...current, [name]: value }));
  };

  const addMaterialURL = () => {
    if (materials.length >= MAX_MATERIAL_COUNT) {
      showWarning(`最多添加 ${MAX_MATERIAL_COUNT} 个素材`);
      return;
    }
    const url = materialDraft.url.trim();
    if (!isHTTPURL(url)) {
      showError('请输入可访问的 http/https 素材 URL');
      return;
    }
    const type = materialDraft.type || 'image_url';
    setMaterials((current) => [
      ...current,
      {
        id: `${type}-${url}-${Date.now()}`,
        type,
        url,
        role: defaultRoleForMaterialType(type),
      },
    ]);
    setMaterialDraft((current) => ({ ...current, url: '' }));
  };

  const addMaterialAssetToMaterials = (asset) => {
    if (!asset?.public_url) {
      showError('素材 URL 不可用');
      return false;
    }
    if (materials.some((item) => item.url === asset.public_url)) {
      return true;
    }
    if (materials.length >= MAX_MATERIAL_COUNT) {
      showWarning(`最多添加 ${MAX_MATERIAL_COUNT} 个素材`);
      return false;
    }
    const type = asset.type || 'image_url';
    setMaterials((current) => [
      ...current,
      {
        id: `asset-${asset.id}-${Date.now()}`,
        type,
        url: asset.public_url,
        role: defaultRoleForMaterialType(type),
      },
    ]);
    return true;
  };

  const referenceMaterialInPrompt = (asset) => {
    if (!addMaterialAssetToMaterials(asset)) {
      return;
    }
    const name = materialAssetName(asset);
    setVideoPrompt((current) => {
      const atIndex = lastMaterialMentionIndex(current);
      const marker = `素材「${name}」`;
      if (atIndex < 0) {
        return `${current}${current ? '\n' : ''}${marker}`;
      }
      const before = current.slice(0, atIndex);
      const after = current.slice(atIndex + 1);
      const suffixIndex = after.search(/[\s\n，。；、,.!?！？]/);
      const suffix = suffixIndex >= 0 ? after.slice(suffixIndex) : '';
      return `${before}${marker}${suffix}`;
    });
    setMaterialReferenceOpen(false);
    showSuccess('已引用素材并加入生成参数');
  };

  const applyMaterialAssetToTarget = (asset, target = 'material') => {
    if (!asset?.public_url) {
      showError('素材 URL 不可用');
      return false;
    }
    if (target === 'first_frame' || target === 'last_frame') {
      if (asset.type !== 'image_url') {
        showError('首尾帧只能选择图片素材');
        return false;
      }
      if (target === 'first_frame') {
        setFirstFrameUrl(asset.public_url);
        showSuccess('已填入首帧 URL');
      } else {
        setLastFrameUrl(asset.public_url);
        showSuccess('已填入尾帧 URL');
      }
      return true;
    }
    setMaterialDraft({
      type: asset.type || 'image_url',
      url: asset.public_url,
    });
    showSuccess('已填入素材 URL');
    return true;
  };

  const triggerMaterialUpload = (target = 'material') => {
    materialUploadTargetRef.current = target;
    if (materialUploadInputRef.current) {
      materialUploadInputRef.current.accept =
        target === 'first_frame' || target === 'last_frame'
          ? 'image/*'
          : 'image/*,video/*,audio/*';
      materialUploadInputRef.current.click();
    }
  };

  const uploadMaterialToLibrary = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    const target = materialUploadTargetRef.current || 'material';
    if ((target === 'first_frame' || target === 'last_frame') && !String(file.type || '').startsWith('image/')) {
      showError('首尾帧只能上传图片素材');
      if (materialUploadInputRef.current) {
        materialUploadInputRef.current.value = '';
      }
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name);
    formData.append('type', materialDraft.type || 'image_url');
    setUploadingMaterial(true);
    try {
      const res = await API.post('/api/materials/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (!res.data?.success) {
        showError(res.data?.message || '素材上传失败');
        return;
      }
      const asset = res.data.data;
      setMaterialLibrary((current) => [asset, ...current.filter((item) => item.id !== asset.id)]);
      applyMaterialAssetToTarget(asset, target);
    } catch (error) {
      showError(error?.response?.data?.message || error?.message || '素材上传失败');
    } finally {
      setUploadingMaterial(false);
      if (materialUploadInputRef.current) {
        materialUploadInputRef.current.value = '';
      }
    }
  };

  const deleteMaterialFromLibrary = async (asset) => {
    if (!asset?.id) {
      return;
    }
    if (!window.confirm(`确认删除素材「${asset.name || asset.original_name || asset.id}」？`)) {
      return;
    }
    try {
      const res = await API.delete(`/api/materials/${asset.id}`);
      if (!res.data?.success) {
        showError(res.data?.message || '素材删除失败');
        return;
      }
      setMaterialLibrary((current) => current.filter((item) => item.id !== asset.id));
      if (materialDraft.url === asset.public_url) {
        setMaterialDraft((current) => ({ ...current, url: '' }));
      }
      if (firstFrameUrl === asset.public_url) {
        setFirstFrameUrl('');
      }
      if (lastFrameUrl === asset.public_url) {
        setLastFrameUrl('');
      }
      showSuccess('素材已删除');
    } catch (error) {
      showError(error?.response?.data?.message || error?.message || '素材删除失败');
    }
  };

  const removeMaterial = (id) => {
    setMaterials((current) => current.filter((item) => item.id !== id));
  };

  const clearVideoDraft = () => {
    setVideoPrompt('');
    setMaterials([]);
    setMaterialDraft({
      type: 'image_url',
      url: '',
    });
    setFirstFrameUrl('');
    setLastFrameUrl('');
    setVideoParams({
      ratio: '16:9',
      resolution: '720P',
      duration: 11,
      style: '不限',
      audio: true,
    });
    setLatestVideoResult(null);
  };

  const updateVideoHistoryRecord = useCallback((record) => {
    setVideoHistory((current) => {
      const next = current.map((item) =>
        item.id === record.id ||
        (record.taskId && item.taskId === record.taskId) ||
        (record.localId && item.id === record.localId)
          ? record
          : item
      );
      saveLocalHistory(next);
      return next;
    });
  }, []);

  const buildVideoMaterials = () => {
    const nextMaterials = [...materials];
    if (firstFrameUrl.trim()) {
      nextMaterials.push({
        type: 'image_url',
        url: firstFrameUrl.trim(),
        role: 'reference_image',
      });
    }
    if (lastFrameUrl.trim()) {
      nextMaterials.push({
        type: 'image_url',
        url: lastFrameUrl.trim(),
        role: 'reference_image',
      });
    }
    return nextMaterials;
  };

  const buildVideoPayload = (prompt, seedanceMaterials, wait = false) => ({
    model: selectedVideoModel,
    prompt,
    materials: seedanceMaterials,
    generate_audio: videoParams.audio,
    ratio: videoParams.ratio,
    resolution: videoParams.resolution,
    duration: videoParams.duration,
    watermark: false,
    wait,
    download: true,
    poll_interval_seconds: 15,
    timeout_seconds: 900,
  });

  useEffect(() => {
    const prompt = videoPrompt.trim();
    if (!selectedVideoModel || !prompt) {
      setVideoEstimate(null);
      return undefined;
    }
    const seedanceMaterials = buildVideoMaterials();
    const invalidURL = seedanceMaterials.find((item) => !isHTTPURL(item.url));
    if (invalidURL) {
      setVideoEstimate(null);
      return undefined;
    }
    const timer = setTimeout(async () => {
      setEstimateLoading(true);
      try {
        const res = await API.post(
          '/api/video-generation/estimate',
          buildVideoPayload(prompt, seedanceMaterials, false)
        );
        if (res.data?.success) {
          setVideoEstimate(res.data.data);
        }
      } catch (error) {
        setVideoEstimate(null);
      } finally {
        setEstimateLoading(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [
    selectedVideoModel,
    videoPrompt,
    materials,
    firstFrameUrl,
    lastFrameUrl,
    videoParams,
  ]);

  const pollVideoTask = useCallback(async (taskId, baseRecord) => {
    const startedAt = Date.now();
    while (Date.now() - startedAt < 15 * 60 * 1000) {
      const res = await API.get(`/api/video-generation/tasks/${encodeURIComponent(taskId)}`);
      if (res.data?.success && res.data.data) {
        const taskRecord = taskToHistoryRecord(res.data.data);
        const mergedRecord = {
          ...baseRecord,
          ...taskRecord,
          status: taskRecord.status,
        };
        updateVideoHistoryRecord(mergedRecord);
        setLatestVideoResult(mergedRecord);
        if (['succeeded', 'failed', 'refunded', 'timeout'].includes(taskRecord.statusRaw)) {
          return mergedRecord;
        }
      }
      await sleep(5000);
    }
    return {
      ...baseRecord,
      status: '生成中',
      statusRaw: 'processing',
      error: '',
    };
  }, [updateVideoHistoryRecord]);

  useEffect(() => {
    const activeTask = videoHistory.find(
      (item) => item.taskId && ['queued', 'processing'].includes(item.statusRaw)
    );
    if (!activeTask || backgroundPollingTaskId || videoGenerating) {
      return undefined;
    }
    setBackgroundPollingTaskId(activeTask.taskId);
    setLatestVideoResult(activeTask);
    setVideoGenerating(true);
    pollVideoTask(activeTask.taskId, activeTask)
      .then((finishedRecord) => {
        if (!mountedRef.current) return;
        if (finishedRecord.statusRaw === 'succeeded') {
          showSuccess('后台视频生成已完成');
        } else if (finishedRecord.error) {
          showError(finishedRecord.error);
        }
      })
      .finally(() => {
        if (mountedRef.current) {
          setVideoGenerating(false);
          setBackgroundPollingTaskId('');
        }
      });
    return undefined;
  }, [backgroundPollingTaskId, pollVideoTask, videoGenerating, videoHistory]);

  const submitVideoGeneration = async () => {
    if (!selectedVideoModel) {
      showError('暂无可用视频模型，请先在模型管理中配置视频生成模型');
      return;
    }
    if (!activeToken?.id) {
      showError('请先在统一模型调用入口保存 API 令牌');
      navigate('/access');
      return;
    }
    if (activeToken.status !== 1) {
      showError('保存的 API 令牌当前不可用，请在统一模型调用入口重新保存可用令牌');
      navigate('/access');
      return;
    }
    const prompt = videoPrompt.trim();
    if (!prompt) {
      showError('请输入视频提示词');
      return;
    }
    const seedanceMaterials = buildVideoMaterials();
    const invalidURL = seedanceMaterials.find((item) => !isHTTPURL(item.url));
    if (invalidURL) {
      showError(`素材 URL 不合法：${invalidURL.url}`);
      return;
    }

    const record = {
      id: `${Date.now()}`,
      prompt,
      model: selectedVideoModel,
      status: '提交中',
      createdAt: Date.now(),
      params: videoParams,
      materials: seedanceMaterials.map((item) => item.url),
      frames: {
        first: firstFrameUrl.trim(),
        last: lastFrameUrl.trim(),
      },
    };
    const nextHistory = [record, ...videoHistory].slice(0, 20);
    setVideoHistory(nextHistory);
    saveLocalHistory(nextHistory);
    setLatestVideoResult(record);
    setVideoGenerating(true);

    try {
      const res = await API.post(
        '/api/video-generation/generations',
        {
          ...buildVideoPayload(prompt, seedanceMaterials, false),
          token_id: activeToken.id,
          idempotency_key: record.id,
        },
        {
          headers: {
            'Idempotency-Key': record.id,
          },
        }
      );
      const data = res?.data?.success ? res.data.data || {} : res?.data || {};
      if (res?.data && res.data.success === false) {
        throw new Error(res.data.message || '视频生成失败');
      }
      if (data.error) {
        throw new Error(data.error.message || '视频生成失败');
      }
      const submittedRecord = {
        ...record,
        localId: record.id,
        taskId: data.id,
        id: data.id || record.id,
        status: taskStatusText(data.status || 'queued'),
        statusRaw: data.status || 'queued',
        videoUrl: data.local_video_url || data.video_url || '',
        upstreamVideoUrl: data.video_url || '',
        elapsedMs: data.elapsed_ms || 0,
        billing: data.billing || null,
      };
      updateVideoHistoryRecord(submittedRecord);
      setLatestVideoResult(submittedRecord);
      showSuccess('视频任务已提交，可稍后在历史记录查看');
      if (submittedRecord.taskId) {
        const finishedRecord = await pollVideoTask(submittedRecord.taskId, submittedRecord);
        if (finishedRecord.statusRaw === 'succeeded') {
          showSuccess('视频生成完成');
        } else if (finishedRecord.error) {
          showError(finishedRecord.error);
        }
      }
    } catch (error) {
      const failedRecord = {
        ...record,
        status: '失败',
        statusRaw: 'failed',
        error: extractErrorMessage(error),
      };
      updateVideoHistoryRecord(failedRecord);
      setLatestVideoResult(failedRecord);
      showError(extractErrorMessage(error));
    } finally {
      setVideoGenerating(false);
    }
  };

  const sendChat = async () => {
    const content = chatInput.trim();
    if (!content || chatSending) {
      return;
    }
    if (!bearerToken) {
      showError('请先创建或启用 API 令牌');
      navigate('/token/add');
      return;
    }
    if (!selectedChatModel) {
      showError('暂无可用语言模型，请先在模型管理中启用语言模型');
      return;
    }

    const nextMessages = [
      ...messages,
      {
        role: 'user',
        content,
      },
    ];
    setMessages(nextMessages);
    setChatInput('');
    setChatSending(true);

    if (isRealtimeLanguageModel(selectedChatModel)) {
      try {
        await testRealtimeConnection(selectedChatModel, bearerToken);
        setMessages((current) => [
          ...current,
          {
            role: 'assistant',
            content:
              `Realtime 连接测试成功：${selectedChatModel} 是实时语音 WebSocket 模型，需要按 /v1/realtime/dialogue?model=${selectedChatModel} 建立连接，并发送 16000Hz 单声道 PCM 音频。当前文本框不会把文字转成语音流。`,
          },
        ]);
      } catch (error) {
        setMessages((current) => [
          ...current,
          {
            role: 'assistant',
            content: `调用失败：${extractErrorMessage(error)}`,
            error: true,
          },
        ]);
      } finally {
        setChatSending(false);
      }
      return;
    }

    try {
      const res = await API.post(
        '/v1/chat/completions',
        {
          model: selectedChatModel,
          messages: nextMessages.map(({ role, content: text }) => ({ role, content: text })),
          stream: false,
          temperature: 0.7,
        },
        {
          headers: {
            Authorization: `Bearer ${bearerToken}`,
          },
        }
      );
      const data = res?.data || {};
      const reply =
        data.choices?.[0]?.message?.content ||
        data.choices?.[0]?.text ||
        data.message ||
        '模型已返回空内容';
      setMessages((current) => [...current, { role: 'assistant', content: reply }]);
    } catch (error) {
      setMessages((current) => [
        ...current,
        {
          role: 'assistant',
          content: `调用失败：${extractErrorMessage(error)}`,
          error: true,
        },
      ]);
    } finally {
      setChatSending(false);
    }
  };

  const handleChatKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendChat();
    }
  };

  return (
    <div className='sino-playground'>
      <div className='playground-head'>
        <div>
          <h2>体验中心</h2>
          <p>选择模型，输入提示词，快速验证语言对话和视频生成配置。</p>
        </div>
        <div className='playground-mode-switch'>
          <Button
            className={mode === 'video' ? 'active' : ''}
            onClick={() => setMode('video')}
          >
            <Icon name='video camera' />
            视频生成
          </Button>
          <Button
            className={mode === 'chat' ? 'active' : ''}
            onClick={() => setMode('chat')}
          >
            <Icon name='comments outline' />
            语言对话
          </Button>
        </div>
      </div>

      {loading ? (
        <div className='playground-loading'>
          <Loader active inline='centered' />
        </div>
      ) : mode === 'video' ? (
        <div className='playground-video-shell'>
          <section className='playground-video-main'>
            <div className='playground-empty-title'>
              <span className='playground-title-icon'>
                <Icon name='video play' />
              </span>
              <h3>视频生成 Playground</h3>
              <p>选择模型，输入提示词，开始生成视频</p>
            </div>

            <div className='playground-field'>
              <div className='playground-label-row'>
                <label>提示词</label>
                <span>{videoPrompt.length} / {MAX_PROMPT_LENGTH}</span>
              </div>
              <div className='playground-template-row'>
                {videoPromptTemplates.map((template) => (
                  <Button
                    key={template.key}
                    type='button'
                    size='mini'
                    basic
                    onClick={() => setVideoPrompt(template.prompt)}
                  >
                    {template.title}
                  </Button>
                ))}
                <Button
                  type='button'
                  size='mini'
                  basic
                  onClick={() => {
                    setMaterialReferenceOpen((current) => !current);
                    if (!materialLibrary.length) {
                      loadMaterialLibrary();
                    }
                  }}
                >
                  <Icon name='paperclip' />
                  素材引用
                </Button>
              </div>
              <textarea
                value={videoPrompt}
                maxLength={MAX_PROMPT_LENGTH}
                onChange={(event) => setVideoPrompt(event.target.value)}
                placeholder='描述你想生成的视频内容... 输入 @ 或 ＠ 可快速引用已上传的图片、音频、视频素材'
              />
              {materialReferenceActive && (
                <div className='playground-reference-picker'>
                  <div className='playground-reference-head'>
                    <span>
                      <Icon name='at' />
                      素材引用
                    </span>
                    <small>点选后自动加入生成素材 URL</small>
                  </div>
                  {materialReferenceOptions.length > 0 ? (
                    <div className='playground-reference-list'>
                      {materialReferenceOptions.map((asset) => (
                        <button
                          type='button'
                          className='playground-reference-item'
                          key={asset.id}
                          onClick={() => referenceMaterialInPrompt(asset)}
                        >
                          <Icon name={iconForMaterialType(asset.type)} />
                          <span>
                            <strong>{materialAssetName(asset)}</strong>
                            <em>{asset.mime_type || asset.type} · {formatMaterialSize(asset.size)}</em>
                          </span>
                          <b>引用</b>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className='playground-reference-empty'>
                      {materialLibrary.length > 0 ? '没有匹配的素材' : '素材库为空，请先上传图片、音频或视频'}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className='playground-upload-panel'>
              <div className='playground-label-row'>
                <label>素材 URL（{materials.length}/{MAX_MATERIAL_COUNT}）</label>
                <Button basic size='small' onClick={addMaterialURL}>
                  <Icon name='plus' />
                  添加素材
                </Button>
              </div>
              <div className='playground-url-row'>
                <Dropdown
                  selection
                  options={materialTypeOptions}
                  value={materialDraft.type}
                  onChange={(_, { value }) => updateMaterialDraft('type', value)}
                />
                <input
                  value={materialDraft.url}
                  onChange={(event) => updateMaterialDraft('url', event.target.value)}
                  placeholder='https://...'
                />
              </div>
              <div className='playground-material-actions'>
                <input
                  ref={materialUploadInputRef}
                  type='file'
                  accept='image/*,video/*,audio/*'
                  onChange={uploadMaterialToLibrary}
                  hidden
                />
                <Button
                  basic
                  size='small'
                  loading={uploadingMaterial}
                  disabled={uploadingMaterial}
                  onClick={() => triggerMaterialUpload('material')}
                >
                  <Icon name='cloud upload' />
                  上传到素材库
                </Button>
                <Button
                  basic
                  size='small'
                  loading={materialLibraryLoading}
                  disabled={materialLibraryLoading}
                  onClick={loadMaterialLibrary}
                >
                  <Icon name='refresh' />
                  刷新素材库
                </Button>
              </div>
              <div className='playground-material-library'>
                <div className='playground-material-library-head'>
                  <span>我的素材库</span>
                  <small>{materialLibrary.length} 个素材</small>
                </div>
                {materialLibraryLoading ? (
                  <div className='playground-material-empty'>
                    <Loader active inline size='small' />
                    <span>正在加载素材库...</span>
                  </div>
                ) : materialLibrary.length > 0 ? (
                  <div className='playground-material-list'>
                    {materialLibrary.map((asset) => (
                      <div className='playground-material-item' key={asset.id}>
                        <Icon name={iconForMaterialType(asset.type)} />
                        <div>
                          <strong>{materialAssetName(asset)}</strong>
                          <span>{asset.mime_type || asset.type} · {formatMaterialSize(asset.size)}</span>
                        </div>
                        <Button size='mini' basic onClick={() => applyMaterialAssetToTarget(asset, 'material')}>
                          素材 URL
                        </Button>
                        <Button size='mini' basic color='blue' onClick={() => referenceMaterialInPrompt(asset)}>
                          引用
                        </Button>
                        <Button
                          size='mini'
                          basic
                          disabled={asset.type !== 'image_url'}
                          onClick={() => applyMaterialAssetToTarget(asset, 'first_frame')}
                        >
                          首帧
                        </Button>
                        <Button
                          size='mini'
                          basic
                          disabled={asset.type !== 'image_url'}
                          onClick={() => applyMaterialAssetToTarget(asset, 'last_frame')}
                        >
                          尾帧
                        </Button>
                        <Button size='mini' basic color='red' onClick={() => deleteMaterialFromLibrary(asset)}>
                          删除
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className='playground-material-empty'>
                    <span>暂无素材，上传图片、视频或音频后会出现在这里。</span>
                  </div>
                )}
              </div>
              {materials.length > 0 && (
                <div className='playground-file-list'>
                  {materials.map((item) => (
                    <span className='playground-file-chip' key={item.id}>
                      <Icon name={iconForMaterialType(item.type)} />
                      {item.url}
                      <button type='button' onClick={() => removeMaterial(item.id)}>
                        <Icon name='close' />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className='playground-model-param-grid'>
              <div className='playground-side-block'>
                <label>模型选择</label>
                <Dropdown
                  placeholder='请选择视频模型'
                  fluid
                  selection
                  options={videoModelOptions}
                  value={selectedVideoModel}
                  onChange={(_, { value }) => setSelectedVideoModel(value)}
                  noResultsMessage='暂无视频模型'
                />
                {!videoModels.length && (
                  <Message size='small' warning>
                    当前账号暂无视频模型，请在后台模型管理中启用视频生成模型后再提交任务。
                  </Message>
                )}
              </div>

              <div className='playground-side-block'>
                <label>首尾帧（可选）</label>
                <div className='playground-frame-row'>
                  <input
                    value={firstFrameUrl}
                    onChange={(event) => setFirstFrameUrl(event.target.value)}
                    placeholder='首帧图片 URL'
                  />
                  <input
                    value={lastFrameUrl}
                    onChange={(event) => setLastFrameUrl(event.target.value)}
                    placeholder='尾帧图片 URL'
                  />
                </div>
                <div className='playground-frame-actions'>
                  <Button
                    basic
                    size='small'
                    loading={uploadingMaterial && materialUploadTargetRef.current === 'first_frame'}
                    disabled={uploadingMaterial}
                    onClick={() => triggerMaterialUpload('first_frame')}
                  >
                    <Icon name='cloud upload' />
                    上传首帧
                  </Button>
                  <Button
                    basic
                    size='small'
                    loading={uploadingMaterial && materialUploadTargetRef.current === 'last_frame'}
                    disabled={uploadingMaterial}
                    onClick={() => triggerMaterialUpload('last_frame')}
                  >
                    <Icon name='cloud upload' />
                    上传尾帧
                  </Button>
                </div>
              </div>
            </div>

            <div className='playground-param-panel'>
              <div className='playground-panel-title'>参数配置</div>
              <div className='playground-param-grid'>
                <div>
                  <label>画面比例</label>
                  <Dropdown
                    fluid
                    selection
                    options={ratioOptions}
                    value={videoParams.ratio}
                    onChange={(_, { value }) => updateVideoParam('ratio', value)}
                  />
                </div>
                <div>
                  <label>分辨率</label>
                  <Dropdown
                    fluid
                    selection
                    options={resolutionOptions}
                    value={videoParams.resolution}
                    onChange={(_, { value }) => updateVideoParam('resolution', value)}
                  />
                </div>
                <div>
                  <label>时长（{videoParams.duration}秒）</label>
                  <div className='playground-stepper'>
                    <Button
                      icon
                      basic
                      disabled={videoParams.duration <= 1}
                      onClick={() => updateVideoParam('duration', Math.max(1, videoParams.duration - 1))}
                    >
                      <Icon name='minus' />
                    </Button>
                    <strong>{videoParams.duration}</strong>
                    <Button
                      icon
                      basic
                      disabled={videoParams.duration >= 15}
                      onClick={() => updateVideoParam('duration', Math.min(15, videoParams.duration + 1))}
                    >
                      <Icon name='plus' />
                    </Button>
                  </div>
                </div>
                <div>
                  <label>风格</label>
                  <Dropdown
                    fluid
                    selection
                    options={styleOptions}
                    value={videoParams.style}
                    onChange={(_, { value }) => updateVideoParam('style', value)}
                  />
                </div>
                <div className='playground-toggle-row'>
                  <label>生成音频</label>
                  <button
                    type='button'
                    className={videoParams.audio ? 'enabled' : ''}
                    onClick={() => updateVideoParam('audio', !videoParams.audio)}
                  >
                    <span />
                  </button>
                </div>
              </div>
            </div>

            <div className='playground-actions'>
              <div className='playground-estimate'>
                {estimateLoading ? (
                  <span>正在估算费用...</span>
                ) : videoEstimate ? (
                  <>
                    <strong>预计扣费 ¥{formatCurrency(videoEstimate.amount_rmb)}</strong>
                    <span>{videoEstimate.input_scene} · {videoEstimate.resolution?.toUpperCase()} · {formatCurrency(videoEstimate.token_unit_price)}元/百万Tokens</span>
                    {videoEstimate.resource_tokens ? (
                      <span>预计资源包 Tokens {formatNumber(videoEstimate.resource_tokens)}</span>
                    ) : null}
                  </>
                ) : (
                  <span>填写提示词后显示预计费用</span>
                )}
              </div>
              <div className='playground-token-note'>
                <Icon name='key' />
                <span>
                  {activeToken
                    ? `${savedTokenSelected ? '已保存令牌' : '默认令牌'}：${activeToken.name || `令牌 #${activeToken.id}`}`
                    : '未选择 API 令牌'}
                </span>
                <Button as={Link} to='/access' basic size='mini'>
                  切换
                </Button>
              </div>
              <Button basic onClick={clearVideoDraft}>清空</Button>
              <Button primary loading={videoGenerating} disabled={videoGenerating} onClick={submitVideoGeneration}>
                <Icon name='play' />
                提交生成
              </Button>
            </div>

            {latestVideoResult && (
              <div className={`playground-result-panel ${latestVideoResult.error ? 'failed' : ''}`}>
                <div className='playground-result-head'>
                  <div>
                    <span>生成结果</span>
                    <strong>{latestVideoResult.status}</strong>
                  </div>
                  {latestVideoResult.taskId ? <code>{latestVideoResult.taskId}</code> : null}
                </div>
                {videoGenerating && !latestVideoResult.videoUrl ? (
                  <div className='playground-result-wait'>
                    <Loader active inline size='small' />
                    <span>任务已提交，后台会继续生成、下载并结算，关闭网页不会中断。</span>
                  </div>
                ) : null}
                {latestVideoResult.error ? (
                  <Message negative size='small'>
                    {latestVideoResult.error}
                  </Message>
                ) : null}
                {latestVideoResult.warning ? (
                  <Message warning size='small'>
                    {latestVideoResult.warning}
                  </Message>
                ) : null}
                {latestVideoResult.videoUrl ? (
                  <>
                    <video src={latestVideoResult.videoUrl} controls playsInline />
                    <div className='playground-result-meta'>
                      <span>{latestVideoResult.params.ratio}</span>
                      <span>{latestVideoResult.params.resolution}</span>
                      <span>{latestVideoResult.params.duration}秒</span>
                      {latestVideoResult.elapsedMs ? <span>{Math.round(latestVideoResult.elapsedMs / 1000)}秒完成</span> : null}
                      {latestVideoResult.billing?.amount_rmb ? (
                        <span>扣费 ¥{formatCurrency(latestVideoResult.billing.amount_rmb)}</span>
                      ) : null}
                      {latestVideoResult.billing?.input_scene ? <span>{latestVideoResult.billing.input_scene}</span> : null}
                      {latestVideoResult.billing?.token_unit_price ? (
                        <span>{formatCurrency(latestVideoResult.billing.token_unit_price)}元/百万Tokens</span>
                      ) : null}
                      {latestVideoResult.billing?.model_tokens ? (
                        <span>模型 Tokens {formatNumber(latestVideoResult.billing.model_tokens)}</span>
                      ) : null}
                      {latestVideoResult.billing?.resource_tokens ? (
                        <span>资源包 Tokens {formatNumber(latestVideoResult.billing.resource_tokens)}</span>
                      ) : null}
                      {latestVideoResult.billing?.offset_ratio ? (
                        <span>抵扣 1:{formatRatio(latestVideoResult.billing.offset_ratio)}</span>
                      ) : null}
                      {latestVideoResult.billing?.estimated ? <span>预估计量</span> : null}
                    </div>
                    <div className='playground-result-actions'>
                      <Button as='a' href={latestVideoResult.videoUrl} target='_blank' rel='noreferrer' basic>
                        <Icon name='external alternate' />
                        打开本地视频
                      </Button>
                      <Button as='a' href={latestVideoResult.videoUrl} download basic>
                        <Icon name='download' />
                        下载视频
                      </Button>
                      {latestVideoResult.upstreamVideoUrl && latestVideoResult.upstreamVideoUrl !== latestVideoResult.videoUrl ? (
                        <Button as='a' href={latestVideoResult.upstreamVideoUrl} target='_blank' rel='noreferrer' basic>
                          <Icon name='cloud' />
                          上游链接
                        </Button>
                      ) : null}
                    </div>
                  </>
                ) : null}
              </div>
            )}
          </section>

          <aside className='playground-history-panel'>
            <div className='playground-panel-title'>历史记录</div>
            {videoHistory.length ? (
              <div className='playground-history-list'>
                {videoHistory.map((item) => (
                  <article className='playground-history-item' key={item.id}>
                    <div>
                      <strong>{item.model}</strong>
                      <span>{formatTime(item.createdAt)}</span>
                    </div>
                    <p>{item.prompt}</p>
                    <small>
                      {item.params.ratio} · {item.params.resolution} · {item.params.duration}秒 · {item.status}
                      {item.billing?.amount_rmb
                        ? ` · 扣费 ¥${formatCurrency(item.billing.amount_rmb)}`
                        : ''}
                    </small>
                    {item.error ? <em>{item.error}</em> : null}
                    {item.warning ? <em>{item.warning}</em> : null}
                    {item.videoUrl ? (
                      <>
                        <video src={item.videoUrl} controls playsInline />
                        <div className='playground-history-links'>
                          <a href={item.videoUrl} target='_blank' rel='noreferrer'>
                            打开
                          </a>
                          <a href={item.videoUrl} download>
                            下载
                          </a>
                        </div>
                      </>
                    ) : null}
                  </article>
                ))}
              </div>
            ) : (
              <div className='playground-history-empty'>
                <Icon name='clock outline' />
                <span>暂无历史记录</span>
              </div>
            )}
          </aside>
        </div>
      ) : (
        <div className='playground-chat-shell'>
          <section className='playground-chat-panel'>
            <div className='playground-chat-toolbar'>
              <Dropdown
                placeholder='请选择语言模型'
                selection
                search
                options={modelOptions}
                value={selectedChatModel}
                onChange={(_, { value }) => setSelectedChatModel(value)}
                noResultsMessage='暂无语言模型'
              />
              <Button basic onClick={() => setMessages([])}>
                <Icon name='trash alternate outline' />
                清空对话
              </Button>
            </div>

            <div className='playground-chat-body'>
              {messages.length ? (
                messages.map((item, index) => (
                  <div
                    className={`playground-message ${item.role === 'user' ? 'user' : 'assistant'} ${item.error ? 'error' : ''}`}
                    key={`${item.role}-${index}`}
                  >
                    <span className='playground-message-avatar'>
                      <Icon name={item.role === 'user' ? 'user outline' : 'paper plane outline'} />
                    </span>
                    <div>{item.content}</div>
                  </div>
                ))
              ) : (
                <div className='playground-chat-empty'>
                  <Icon name='paper plane outline' />
                  <h3>语言模型对话</h3>
                  <p>选择模型，在下方输入消息开始对话</p>
                </div>
              )}
              {chatSending && (
                <div className='playground-message assistant'>
                  <span className='playground-message-avatar'>
                    <Icon name='paper plane outline' />
                  </span>
                  <div>正在生成回复...</div>
                </div>
              )}
            </div>

            <div className='playground-chat-input'>
              {!bearerToken && (
                <Message warning size='small'>
                  当前账号暂无可用 API 令牌，<Link to='/token/add'>创建令牌</Link> 后即可体验语言对话。
                </Message>
              )}
              {bearerToken && !chatModels.length && (
                <Message warning size='small'>
                  当前 API 令牌暂无可用语言模型，请在 API 令牌中开放语言模型，或到模型管理中启用语言模型。
                </Message>
              )}
              {bearerToken && isRealtimeLanguageModel(selectedChatModel) && (
                <Message info size='small'>
                  {selectedChatModel} 是实时语音 WebSocket 模型，发送按钮会先测试 Realtime 连接；正式对话需要接入麦克风 PCM 音频流。
                </Message>
              )}
              <textarea
                value={chatInput}
                onChange={(event) => setChatInput(event.target.value)}
                onKeyDown={handleChatKeyDown}
                placeholder='输入消息， Enter 发送；Shift+Enter 换行'
              />
              <Button primary disabled={!chatInput.trim() || chatSending} loading={chatSending} onClick={sendChat}>
                <Icon name='send' />
                发送
              </Button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default Playground;
